import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	CircularProgress,
	Box,
} from "../../../common/MaterialComponent";
import { IVRFlowTypeConfiguration } from "./../IVRFlowTypeConfiguration/IVRFlowTypeConfiguration";
import { IVRFlowWizardConfiguration } from "./../IVRFlowWizardConfiguration/IVRFlowWizardConfiguration";
import { IVRStudioFlow } from "./../IVRStudioFlow/IVRStudioFlow";
import {
	GenerateScript,
	GetPreSubmissionConfigurationAsync,
	SavePreSubmissionConfigurationDetail,
	UploadAudioFilesAsync,
} from "../../../api/batchNotificationController";
import { TemplateType } from "../../../common/enum";
import Shared from "../../../common/shared";
import { ErrorPage } from "../../ErrorPage/ErrorPage";
import { ButtonPrimary } from "../../../common/sharedStyle";
import { PreSubmissionConfigurationModel } from "../../../models/BatchMessage/BatchMessageDetailModel";
import "./IVRFlowConfiguration.css";
import { useHistory } from "react-router-dom";
import {
	FORBIDDEN_RESPONSE_STATUS,
	IVR_FLOW_CONTAINER_NAME,
} from "../../../common/constants";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import UploadAudioFileRequestModel, {
	AudioFileDetailModel,
} from "../../../models/RequestForHelpTemplates/UploadAudioFileRequestModel";
import {
	GetAudioFileList,
	ValidateIVRFlowDetail,
	Action,
	FlowWizardState,
	IVRFlowWizardContext,
	useIVRFlowWidgetConfiguration,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import IVRFlowConfigurationModel from "../../../models/RequestForHelpTemplates/IVRFlowConfigurationModel";
import { USSDScriptDialog } from "../USSDScriptDialog/USSDScriptDialog";

export interface IVRFlowConfigurationProp {
	rowKey: string;
	partitionKey: string;
}

export const IVRFlowConfiguration = (props: IVRFlowConfigurationProp) => {
	const [isMounted, setMounted] = React.useState(false);
	const [error, setError] = React.useState<string>("");
	const [
		flowConfigurationDetail,
		setFlowConfigurationDetail,
	] = useState<PreSubmissionConfigurationModel>(
		new PreSubmissionConfigurationModel()
	);
	const [highlightRequiredDetail, setHighlightRequiredDetail] = useState(false);
	const [hasFlowConfigChanged, setHasFlowConfigChanged] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const history = useHistory();
	const strings = useGetComponentStringsContext().IVRFlowConfiguration;
	const [
		widgetIdToBeValidated,
		setWidgetIdToBeValidated,
	] = React.useState<string>("");
	// generate USSD script
	const [generateScriptLoading, setGenerateScriptLoading] = useState(false);
	const [openUSSDScriptDialog, setOpenUSSDScriptDialog] = useState(false);
	const [scriptDetail, setScriptDetail] = useState<string>("");

	// use reducer for all state logic
	const [state, dispatch] = useIVRFlowWidgetConfiguration();
	const contextValue: {
		state: FlowWizardState;
		dispatch: React.Dispatch<Action>;
	} = {
		state,
		dispatch,
	};

	useEffect(() => {
		const getBatchMessagingDetail = async (
			partitionKey: string,
			rowKey: string
		) => {
			if (!partitionKey || !rowKey) {
				setMounted(true);
				return;
			}
			try {
				const res = await GetPreSubmissionConfigurationAsync(
					partitionKey,
					rowKey
				);
				if (res.responseObj) {
					res.responseObj.ivrFlowJson = Object.assign(
						new IVRFlowConfigurationModel(),
						JSON.parse(res.responseObj.ivrFlowJson.toString())
					);

					setFlowConfigurationDetail(res.responseObj);
					setMounted(true);
				} else {
					if (res.error?.includes(FORBIDDEN_RESPONSE_STATUS.toString())) {
						// response status is 403, redirect to access denied page
						history.push({
							pathname: "/accessDenied",
						});
					}
					setError(res.error!);
				}
			} catch (err) {
				setError(err);
			}
		};
		setMounted(false);
		getBatchMessagingDetail(props.partitionKey, props.rowKey);
	}, [history, props.partitionKey, props.rowKey]);

	useEffect(() => {
		setFlowConfigurationDetail((prevFlowConfigurationDetail) => {
			return {
				...prevFlowConfigurationDetail,
				ivrFlowJson: state.wizardConfig,
			};
		});
	}, [state.wizardConfig]);

	const savePreConfigurationDetail = async () => {
		try {
			const configurationModel = Object.assign(
				new PreSubmissionConfigurationModel(),
				flowConfigurationDetail
			);
			// restrict overriding of id property at the time of updating pre submission configuration detail
			configurationModel.rowKey = flowConfigurationDetail.id;
			configurationModel.type = TemplateType[TemplateType.RequestForHelp];
			configurationModel.ivrFlowJson = JSON.stringify(
				configurationModel.ivrFlowJson
			);

			//Call API Request
			const res = await SavePreSubmissionConfigurationDetail(
				configurationModel
			);
			const flowConfiguration: PreSubmissionConfigurationModel = flowConfigurationDetail;
			if (res.responseObj) {
				flowConfiguration.id = res.responseObj.id;
				flowConfiguration.partitionKey = res.responseObj.partitionKey;
				flowConfiguration.rowKey = res.responseObj.rowKey;
			}

			Shared.showSnackbarMessage(
				res.responseObj
					? strings.saveFlowDetailSuccessMessage
					: strings.saveFlowDetailErrorMessage
			);

			return res.responseObj ? flowConfiguration : undefined;
		} catch (err) {
			Shared.showSnackbarMessage(strings.saveFlowDetailErrorMessage);
		}
	};

	const submitPreConfigurationDetail = async () => {
		const flowConfiguration = await savePreConfigurationDetail();
		if (flowConfiguration) {
			setFlowConfigurationDetail(flowConfiguration);
		}
		setHasFlowConfigChanged(false);
		setLoading(false);
	};

	const onSaveIVRConfiguration = async () => {
		const { isValid, widgetId } = ValidateIVRFlowDetail(
			flowConfigurationDetail
		);

		if (!isValid) {
			if (widgetId) {
				setWidgetIdToBeValidated(widgetId);
			}
			//missing a field so highlight required field in child
			setHighlightRequiredDetail(true);
			// once highlightRequiredDetail prop set to true for component, set value of widgetId to ""
			setTimeout(() => {
				setWidgetIdToBeValidated("");
			}, 0);
		} else if (hasFlowConfigChanged) {
			setLoading(true);
			const audioFileList: AudioFileDetailModel[] = GetAudioFileList(
				flowConfigurationDetail
			);
			if (audioFileList.length > 0) {
				await uploadAudioFilesAsync(audioFileList);
			} else {
				// API call to save Pre Configuration Detail
				submitPreConfigurationDetail();
			}
		}
	};

	const uploadAudioFilesAsync = async (
		audioFileList: AudioFileDetailModel[]
	) => {
		try {
			const uploadAudioFileRequest: UploadAudioFileRequestModel = new UploadAudioFileRequestModel();
			uploadAudioFileRequest.rowKey = flowConfigurationDetail.id;
			uploadAudioFileRequest.partitionKey =
				flowConfigurationDetail.partitionKey;
			uploadAudioFileRequest.files = audioFileList;
			uploadAudioFileRequest.containerName = IVR_FLOW_CONTAINER_NAME;
			// Call API Request
			const res = await UploadAudioFilesAsync(uploadAudioFileRequest);
			if (res.responseObj && res.responseObj.files.length > 0) {
				// map each response file object with widget property and replace fileUrl
				const flowConfigurationDetailTemp = { ...flowConfigurationDetail };
				let ivrFlowJsonValue = Object.values(
					flowConfigurationDetailTemp.ivrFlowJson
				);
				ivrFlowJsonValue = [].concat(...ivrFlowJsonValue);
				res.responseObj.files.forEach((resObject: AudioFileDetailModel) => {
					const widget = ivrFlowJsonValue.find((w) => w?.id === resObject.id);
					if (widget) {
						if (widget[resObject.widgetProperty!].length > 0) {
							const index = widget[resObject.widgetProperty!].findIndex(
								(el: AudioFileModel) =>
									el.language === resObject.language &&
									el.fileName === resObject.fileName
							);
							if (index !== -1) {
								widget[resObject.widgetProperty!][index].value =
									resObject.audioFile;
							}
						} else {
							widget[resObject.widgetProperty!].value = resObject.audioFile;
						}
					}
				});
				// API call to save Pre Configuration Detail
				submitPreConfigurationDetail();
			}
		} catch (err) {
			Shared.showSnackbarMessage(strings.saveFlowDetailErrorMessage);
			setLoading(false);
		}
	};

	const generateScript = async () => {
		try {
			setScriptDetail("");
			setGenerateScriptLoading(true);
			const res = await GenerateScript(
				flowConfigurationDetail.partitionKey,
				flowConfigurationDetail.rowKey
			);
			if (res.responseObj) {
				setScriptDetail(res.responseObj);
				setOpenUSSDScriptDialog(true);
			} else {
				Shared.showSnackbarMessage(strings.ussdScriptErrorMessage);
			}
			setGenerateScriptLoading(false);
		} catch (err) {
			setGenerateScriptLoading(false);
			Shared.showSnackbarMessage(strings.ussdScriptErrorMessage);
		}
	};

	return (
		<div data-testid="ivrFlowConfigurationContainer">
			{error ? (
				<ErrorPage message={strings.errorMessage} />
			) : !isMounted ? (
				<div className="loader">
					<CircularProgress size={50} />
				</div>
			) : (
				<>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography variant="h6">{strings.flowHeader}</Typography>
						</Grid>
						<Grid item xs={12} className="ivrFlowTypeConfig">
							<IVRFlowTypeConfiguration
								flowConfigurationDetail={flowConfigurationDetail}
								onFlowConfigurationDetailChange={setFlowConfigurationDetail}
								highlightRequiredDetail={highlightRequiredDetail}
								setHasFlowConfigChanged={setHasFlowConfigChanged}
							/>
						</Grid>
						<IVRFlowWizardContext.Provider value={contextValue}>
							<Grid item xs={7} className="ivrFlowWidgetConfig">
								<IVRFlowWizardConfiguration
									flowConfigurationDetail={flowConfigurationDetail}
									setHasFlowConfigChanged={setHasFlowConfigChanged}
									hasFlowConfigChanged={hasFlowConfigChanged}
									widgetIdToBeValidated={widgetIdToBeValidated}
								/>
							</Grid>
							<Grid item xs={5} className="ivrStudioFlow">
								<IVRStudioFlow />
							</Grid>
						</IVRFlowWizardContext.Provider>
						<Grid item xs={12}>
							<Box textAlign="right">
								<span className="actionBtnDiv">
									<ButtonPrimary
										data-testid="saveBtn"
										onClick={onSaveIVRConfiguration}
										disabled={isLoading || generateScriptLoading}
									>
										{strings.saveButton}
									</ButtonPrimary>
									{isLoading && (
										<CircularProgress
											size={24}
											className="btnLoader"
											color="primary"
										/>
									)}
								</span>
								{flowConfigurationDetail.id &&
									flowConfigurationDetail.ivrFlowType === "USSD" && (
										<span className="actionBtnDiv generateScriptBtnDiv">
											<ButtonPrimary
												data-testid="generateScriptBtn"
												onClick={generateScript}
												disabled={
													generateScriptLoading ||
													isLoading ||
													hasFlowConfigChanged
												}
											>
												{strings.generateScriptButton}
											</ButtonPrimary>
											{generateScriptLoading && (
												<CircularProgress
													size={24}
													className="btnLoader"
													color="primary"
												/>
											)}
										</span>
									)}
							</Box>
						</Grid>
					</Grid>
				</>
			)}
			<USSDScriptDialog
				open={openUSSDScriptDialog}
				scriptDetail={scriptDetail}
				ivrFlowTitle={flowConfigurationDetail.title}
				closeDialog={() => setOpenUSSDScriptDialog(false)}
			></USSDScriptDialog>
		</div>
	);
};
